import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Formik } from "formik";
import * as Yup from "yup";
import InnerLayout from "../../components/layouts/inner-layout";
import LoadingOverlay from "../../components/helper-components/overlay-loading";
import successToast from "../../components/helper-components/success-toast";
import errorToast from "../../components/helper-components/error-toast";
import NavBar from "../../components/admin-customer/navBar";
import FlowDetails from "./approval-flow-details";
import Originator from "./originator";
import DocumentSection from "./document-and-terms";
import ApprovalStages from "./approval-stages";
import FlowPreview from "./flow-preview";
import {
  getApprovalFlowByIdAction,
  updateApprovalFlowAction,
  updateStagesAction,
  updateDocumentsAction,
  addOriginatorsAction,
  addDocumentsAction,
  addStagesAction,
} from "../../redux/actions/approval-work-flow-action";

const itemTabs = [
  "Flow Details",
  "Originator",
  "Documents & Terms",
  "Stages",
  "Approval Path",
];

// The Stages tab stores a timeline as one of these tokens, while the API stores
// timeline_days as a number. Map between the two in both directions.
const TIMELINE_BY_DAYS = { 3: "<3", 7: "<7", 10: "<10" };

const toOption = (id) => (id ? { label: id, value: id } : null);

/**
 * The get-by-id response nests everything the wizard's tabs need:
 *   { approval_flow_id, name, description, is_active,
 *     originators: [...], document_terms: [...], stages: [{ stages: [...] }] }
 * Reshape it into the exact Formik values those tabs already read, so the same
 * components work unchanged for view and edit.
 */
const toFormValues = (data) => {
  const term = data?.document_terms?.[0] ?? null;
  const stageRow = data?.stages?.[0] ?? null;

  const originators = (data?.originators ?? []).map((o) => ({
    originator_id: o?.originator_id ?? null,
    department: o?.department ?? "",
    departmentObj: toOption(o?.department),
    role: o?.role ?? "",
    roleObj: toOption(o?.role),
    users: o?.user_ids ?? [],
  }));

  return {
    document_id: data?.approval_flow_id ?? null,
    flow_name: data?.name ?? "",
    description: data?.description ?? "",
    is_active: data?.is_active ?? true,

    // Keep the record ids so a save can update in place instead of inserting.
    term_id: term?.term_id ?? null,
    stages_id: stageRow?.stages_id ?? null,

    originators: originators.length
      ? originators
      : [{ departmentObj: null, department: "" }],

    documentCategory: term?.document_category ?? "",
    documentType: term?.document_types?.[0] ?? "",
    conditions: (term?.conditions ?? []).map((c) => ({ ...c, enabled: true })),

    approvalStages: (stageRow?.stages ?? []).map((st) => ({
      role: st?.role ?? "",
      roleObj: toOption(st?.role),
      role_name: st?.role_name ?? "",
      timeline: TIMELINE_BY_DAYS[st?.timeline_days] ?? `<${st?.timeline_days ?? ""}`,
      users: (st?.user_ids ?? []).map((u) => ({ value: u, label: u })),
      actions: (st?.actions ?? []).reduce(
        (acc, a) => ({ ...acc, [String(a).toLowerCase()]: true }),
        {}
      ),
    })),
  };
};

// Mirrors the create wizard's conversion so both screens send stages the same way.
const convertStagesData = (input) =>
  (input ?? []).map((item) => {
    const timeline_days = parseInt(String(item.timeline).match(/\d+/)?.[0] || 0, 10);
    const actions = Object.entries(item.actions ?? {})
      .filter(([, value]) => value)
      .map(([key]) => key.charAt(0).toUpperCase() + key.slice(1));
    return {
      role: item.roleObj?.label || item.role,
      timeline_days,
      user_ids: (item.users ?? []).map((u) =>
        typeof u === "object" ? u.value : u
      ),
      actions,
    };
  });

const ApprovalWorkFlowViewEdit = ({ mode = "view" }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const formikRef = useRef(null);
  const isView = mode === "view";

  const [isLoading, setIsLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState("Flow Details");
  const [initialValues, setInitialValues] = useState(toFormValues(null));
  // The originator ids the server last returned, so a save can tell whether a
  // stored row was dropped from the form.
  const loadedOriginatorIds = useRef([]);

  const loadFlow = () => {
    setIsLoading(true);
    dispatch(
      getApprovalFlowByIdAction(
        { approvalFlowId: id },
        (res) => {
          setIsLoading(false);
          const formValues = toFormValues(res?.data);
          loadedOriginatorIds.current = formValues.originators
            .map((item) => item?.originator_id)
            .filter(Boolean);
          setInitialValues(formValues);
        },
        (err) => {
          setIsLoading(false);
          errorToast(err?.data?.message ?? "Failed to load approval workflow");
        }
      )
    );
  };

  useEffect(() => {
    if (id) loadFlow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const validationSchema = Yup.object().shape({
    flow_name: Yup.string().required("Name is required"),
  });

  const onSaved = (data) => {
    setIsLoading(false);
    successToast(data?.message ?? "Saved successfully");
    loadFlow();
  };
  const onSaveError = (data) => {
    setIsLoading(false);
    errorToast(data?.data?.message ?? data?.message ?? "Something went wrong");
  };

  const onSubmit = (values) => {
    if (isView) return;
    setIsLoading(true);

    if (currentTab === "Flow Details") {
      if (!values.flow_name?.trim()) {
        setIsLoading(false);
        errorToast("Please enter a valid name");
        return;
      }
      dispatch(
        updateApprovalFlowAction(
          {
            approvalFlowId: values.document_id ?? id,
            name: values.flow_name,
            description: values.description,
            is_active: values.is_active,
          },
          onSaved,
          onSaveError
        )
      );
      return;
    }

    if (currentTab === "Originator") {
      // The upsert endpoint only updates or inserts the rows it is given, and the
      // approval-flows API has no delete or replace for originators. Saving a form
      // that dropped a stored row would show a success toast and then hand the row
      // back on reload, so refuse the save instead of losing the removal. The
      // Originator table already blocks removing saved rows; this is the backstop.
      const submittedIds = new Set(
        (values.originators ?? []).map((item) => item?.originator_id).filter(Boolean)
      );
      const removedIds = loadedOriginatorIds.current.filter(
        (originatorId) => !submittedIds.has(originatorId)
      );
      if (removedIds.length) {
        setIsLoading(false);
        errorToast(
          "Saved originators can't be removed yet. Please restore the removed row before saving."
        );
        return;
      }

      // Originators upsert through the same endpoint the create flow uses —
      // rows carrying an originator_id update, rows without it insert.
      const payload = (values.originators ?? []).map((item) => ({
        ...(item?.originator_id ? { originator_id: item.originator_id } : {}),
        approval_flow_id: values.document_id ?? id,
        department: item.department,
        role: item.role,
        user_ids: item.users,
      }));
      if (!(payload[0]?.department && payload[0]?.role && payload[0]?.user_ids?.length)) {
        setIsLoading(false);
        errorToast("Please add atleast one originator");
        return;
      }
      dispatch(addOriginatorsAction(payload, onSaved, onSaveError));
      return;
    }

    if (currentTab === "Documents & Terms") {
      const conditions = (values.conditions ?? [])
        .filter((cond) => cond.enabled)
        .map(({ field, aggregator, value }) => ({ field, aggregator, value }));

      if (!values.documentCategory || conditions.length <= 0) {
        setIsLoading(false);
        errorToast("Please select at one document");
        return;
      }

      // An existing term is updated by term_id; a flow saved without one falls
      // back to the create endpoint.
      if (values.term_id) {
        dispatch(
          updateDocumentsAction(
            {
              term_id: values.term_id,
              document_category: values.documentCategory,
              document_types: [values.documentType],
              conditions,
            },
            onSaved,
            onSaveError
          )
        );
      } else {
        dispatch(
          addDocumentsAction(
            {
              approval_flow_id: values.document_id ?? id,
              document_category: values.documentCategory,
              document_types: [values.documentType],
              conditions,
            },
            onSaved,
            onSaveError
          )
        );
      }
      return;
    }

    if (currentTab === "Stages") {
      const stages = convertStagesData(values.approvalStages);
      if (!stages.length) {
        setIsLoading(false);
        errorToast("Please add atleast one stage");
        return;
      }
      if (values.stages_id) {
        dispatch(
          updateStagesAction(
            { stageId: values.stages_id, stages },
            onSaved,
            onSaveError
          )
        );
      } else {
        dispatch(
          addStagesAction(
            { approval_flow_id: values.document_id ?? id, stages },
            onSaved,
            onSaveError
          )
        );
      }
      return;
    }

    // Approval Path is a read-only preview of the tabs above it.
    setIsLoading(false);
    navigate("/approval-work-flow/list");
  };

  return (
    <InnerLayout
      title={isView ? "View Approval Work Flow" : "Edit Approval Work Flow"}
      backBtn={true}
      {...(isView
        ? { view: true, onclkEditFn: () => navigate(`/approval-work-flow/edit/${id}`) }
        : {})}
    >
      <div className="px-5">
        <NavBar
          generalCreated={true}
          tabs={itemTabs}
          currentTab={currentTab}
          setCurrentTab={setCurrentTab}
          isNotFirstTabAllowed={true}
        />

        <Formik
          enableReinitialize={true}
          onSubmit={onSubmit}
          initialValues={initialValues}
          validationSchema={validationSchema}
          innerRef={formikRef}
        >
          {({ handleSubmit }) => (
            <form
              onSubmit={handleSubmit}
              className="grid grid-rows-[1fr,auto] min-h-[calc(100vh-140px)] overflow-hidden"
            >
              <LoadingOverlay isLoading={isLoading} />

              {/* The tab components are shared with the create wizard and take no
                  disabled prop, so view mode makes the whole area inert rather
                  than forking five components into read-only variants. */}
              <div
                className={`row-start-1 overflow-y-auto p-4 pr-2 ${
                  isView ? "pointer-events-none select-none" : ""
                }`}
                aria-disabled={isView}
              >
                {currentTab === "Flow Details" && <FlowDetails />}
                {currentTab === "Originator" && <Originator />}
                {currentTab === "Documents & Terms" && <DocumentSection />}
                {currentTab === "Stages" && <ApprovalStages />}
                {currentTab === "Approval Path" && <FlowPreview />}
              </div>

              <div className="row-start-2 bg-background px-4 py-3 pb-[calc(env(safe-area-inset-bottom,0px)+12px)] mt-5">
                <div className="flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => navigate("/approval-work-flow/list")}
                    className="text-primary border border-primary px-4 py-2 rounded-md text-sm flex items-center gap-1 hover:bg-primary hover:text-white transition-all duration-300"
                  >
                    {isView ? "Back" : "Cancel"}
                  </button>
                  {!isView && (
                    <button
                      type="submit"
                      className="bg-primary border border-primary text-white px-5 py-2 rounded-md text-sm flex items-center gap-1 hover:bg-white hover:text-primary transition-all duration-300"
                    >
                      Save
                    </button>
                  )}
                </div>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </InnerLayout>
  );
};

export default ApprovalWorkFlowViewEdit;
